import { ref, Ref } from 'vue'
import api from '@/api'
import util from '@/helpers/util'
import type { ConfigWithExtras } from './useWhatsAppConfigurations'

export type ConfigMediaFileInputs = Ref<Record<number, HTMLInputElement | null>>

export function useWhatsAppMedia(openConfigurations: Ref<ConfigWithExtras[]>) {
    const globalMediaUrl = ref<string | null>(null)
    const globalMediaIsImage = ref(false)
    const isUploadingGlobalMedia = ref(false)

    const updateAllMedia = () => {
        if (!globalMediaUrl.value) return
        openConfigurations.value.forEach(config => {
            config.mediaUrl = globalMediaUrl.value
            config.mediaIsImage = globalMediaIsImage.value
        })
    }

    const handleGlobalMediaUpload = async (event: Event) => {
        const target = event.target as HTMLInputElement
        const file = target.files?.[0]
        if (!file) return

        isUploadingGlobalMedia.value = true
        try {
            const formData = new FormData()
            formData.append('file', file)
            const uploadResponse = await api.whatsapp.uploadFile(formData)
            globalMediaUrl.value = uploadResponse.data.url
            globalMediaIsImage.value = file.type.startsWith('image/')
            updateAllMedia()
        } catch (error) {
            console.error('Failed to upload global media:', error)
            util.showToast('Failed to upload media. Please try again.', { icon: 'error', iconClass: 'toast-icon-circle' })
        } finally {
            isUploadingGlobalMedia.value = false
        }
    }

    const clearGlobalMedia = () => {
        globalMediaUrl.value = null
        globalMediaIsImage.value = false
        openConfigurations.value.forEach(config => {
            config.mediaUrl = null
            config.mediaIsImage = false
        })
    }

    const handleConfigMediaUpload = async (event: Event, configIndex: number) => {
        const target = event.target as HTMLInputElement
        const file = target.files?.[0]
        if (!file) return

        const config = openConfigurations.value[configIndex]
        if (!config) return

        config.isUploadingMedia = true
        try {
            const formData = new FormData()
            formData.append('file', file)
            const uploadResponse = await api.whatsapp.uploadFile(formData)
            config.mediaUrl = uploadResponse.data.url
            config.mediaIsImage = file.type.startsWith('image/')
            util.showToast('Media uploaded successfully', { icon: 'check_circle', iconClass: 'toast-icon-circle' })
        } catch (error) {
            console.error('Failed to upload config media:', error)
            util.showToast('Failed to upload media. Please try again.', { icon: 'error', iconClass: 'toast-icon-circle' })
        } finally {
            config.isUploadingMedia = false
            if (target) {
                target.value = ''
            }
        }
    }

    const clearConfigMedia = (configIndex: number) => {
        const config = openConfigurations.value[configIndex]
        if (config) {
            config.mediaUrl = null
            config.mediaIsImage = false
        }
    }

    return {
        globalMediaUrl,
        globalMediaIsImage,
        isUploadingGlobalMedia,
        handleGlobalMediaUpload,
        clearGlobalMedia,
        updateAllMedia,
        handleConfigMediaUpload,
        clearConfigMedia
    }
}

